// IE Hub - API Type Definitions
// Centralized TypeScript interfaces for all API contracts

import type { HumanYesNo } from "../lib/utils/consent";

export type CustomerConsent = HumanYesNo | "";

// Common API Response Structure
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: {
    code: string;
    message: string;
    details?: Record<string, any>;
    traceId?: string;
  };
  timestamp: string;
}

// Pagination
export interface PaginationParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

// Authentication Types
export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface LoginResponse {
  user: User;
  token: string;
  refreshToken: string;
  expiresAt: string;
}

export interface RefreshTokenRequest {
  refresh_token: string; // snake_case per API docs
}

// Response from /api/auth/refresh-token endpoint
export interface RefreshTokenResponse {
  access_token: string;
  refresh_token: string;
  emailAddress: string;
  displayName: string;
  userId: number;
  userName: string;
  forceChangePassword: boolean;
  twoStepVerificationRequired: boolean;
}

export interface ValidateTokenResponse {
  code: string;
  message?: string;
  expiresInSeconds?: number;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
  confirmPassword: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

export interface SignupRequest {
  email: string;
}

export interface SignupResponse {
  verificationCodeSent?: boolean;
  message?: string;
}

export interface MagicLinkRequest {
  email: string;
}

export interface MagicLinkResponse {
  status: string;
  message?: string;
  userId?: number | null;
  orgId?: number | null;
}

export interface VerifyUserRequest {
  token: string;
}

export interface VerifyUserResponse {
  verified: string;
  email: string;
}

export interface GoogleAuthUrlResponse {
  url: string;
  state: string;
}

export interface GoogleExchangeRequest {
  code: string;
  state?: string;
}

export interface GoogleExchangeResponse {
  access_token: string;
  refresh_token: string;
  emailAddress: string;
  displayName: string;
  userId: number;
  userName: string;
  forceChangePassword: boolean;
  twoStepVerificationRequired: boolean;
}

// User Types
export interface User {
  emailAddress: string;
  displayName: string;
  userId: number;
  userName: string;
  forceChangePassword: boolean;
  twoStepVerificationRequired: boolean;
  /**
   * From signin API: "Y" means partner user.
   * Non-partners may omit this field or return "N".
   */
  isPartner?: "Y" | "N" | string;
  onboarding?: {
    completed: boolean;
    currentStep: number;
  };
}

export interface CreateUserRequest {
  email: string;
  name: string;
  password: string;
  roles?: string[];
}

export interface UpdateUserRequest {
  name?: string;
  email?: string;
  roles?: string[];
}

export interface UserSearchParams extends PaginationParams {
  query?: string;
  role?: string;
  status?: "active" | "inactive";
}

// Form/Schema Types
export interface Schema {
  id: string;
  version: string;
  name: string;
  description?: string;
  layout: SchemaLayout;
  fields?: SchemaField[];
  actions?: SchemaActions;
  compute?: SchemaCompute;
  initialComputed?: Record<string, any>;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface SchemaLayout {
  type: "form" | "page" | "dashboard";
  title?: string;
  description?: string;
  sections?: SchemaSection[];
  fields?: SchemaField[];
  [key: string]: any;
}

export interface SchemaSection {
  id: string;
  title?: string;
  description?: string;
  fields: string[];
  visible?: boolean | string;
  [key: string]: any;
}

export interface SchemaField {
  id: string;
  type: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  validation?: Record<string, any>;
  visible?: boolean | string;
  defaultValue?: any;
  options?: Array<{ label: string; value: any }>;
  [key: string]: any;
}

export interface SchemaActions {
  submit?: {
    url: string;
    method: "GET" | "POST" | "PUT" | "DELETE";
    onSuccess?: ActionEffect[];
    onError?: ActionEffect[];
  };
  onSuccess?: ActionEffect[];
  onError?: ActionEffect[];
  [key: string]: any;
}

export interface ActionEffect {
  type: "alert" | "navigate" | "setFieldValue" | "resetForm";
  [key: string]: any;
}

export interface SchemaCompute {
  beforeRender?: Record<string, any>;
  afterSubmit?: Record<string, any>;
  [key: string]: any;
}

export interface FormSubmitRequest {
  schemaId: string;
  data: Record<string, any>;
  timestamp: string;
  clientMetadata?: {
    userAgent: string;
    viewport: { width: number; height: number };
    timezone: string;
  };
}

export interface FormSubmitResponse {
  id: string;
  data: Record<string, any>;
  validationErrors?: Record<string, string>;
  computedValues?: Record<string, any>;
}

export interface FormValidateRequest {
  schemaId: string;
  data: Record<string, any>;
  fieldName?: string;
}

export interface FormValidateResponse {
  valid: boolean;
  errors: Record<string, string>;
  warnings?: Record<string, string>;
}

// File Types
export interface FileUploadRequest {
  file: File;
  category?: string;
  metadata?: Record<string, any>;
}

export interface FileUploadResponse {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
  thumbnailUrl?: string;
}

export interface FileInfo {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
  thumbnailUrl?: string;
  category?: string;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

// System Types
export interface SystemHealth {
  status: "healthy" | "degraded" | "unhealthy";
  version: string;
  uptime: number;
  services: {
    database: "up" | "down";
    redis: "up" | "down";
    storage: "up" | "down";
  };
  timestamp: string;
}

export interface SystemVersion {
  version: string;
  buildDate: string;
  gitCommit: string;
  environment: string;
}

export interface SystemConfig {
  features: Record<string, boolean>;
  limits: {
    maxFileSize: number;
    maxFormFields: number;
    requestTimeout: number;
  };
  client: {
    apiVersion: string;
    supportedFormats: string[];
  };
}

// Monitoring Types
export interface ErrorReport {
  id: string;
  type: string;
  category: string;
  severity: "error" | "warning" | "info";
  message: string;
  stack?: string;
  context: Record<string, any>;
  fingerprint: string;
  count: number;
  firstSeen: string;
  lastSeen: string;
}

export interface MetricData {
  name: string;
  value: number;
  unit: string;
  timestamp: string;
  tags?: Record<string, string>;
}

export interface PerformanceMetric {
  metric: string;
  value: number;
  threshold?: number;
  timestamp: string;
  url: string;
  userAgent: string;
}

// Notification Types
export interface Notification {
  id: string;
  title: string;
  message: string;
  type: "info" | "success" | "warning" | "error";
  read: boolean;
  createdAt: string;
  expiresAt?: string;
  actionUrl?: string;
  actionLabel?: string;
}

export interface NotificationSubscription {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
  userAgent?: string;
  platform?: string;
}

// Request Configuration
export interface RequestConfig {
  timeout?: number;
  retries?: number;
  retryDelay?: number;
  skipAuth?: boolean;
  skipErrorCapture?: boolean;
  headers?: Record<string, string>;
  /**
   * When true, bypasses any in-memory caching in service methods and always
   * performs a fresh HTTP request. Useful for screens (like Budget Breakdown)
   * that must always reflect the latest saved values from IE forms.
   */
  skipCache?: boolean;
}

// Error Types
export interface ApiError extends Error {
  code?: string;
  status?: number;
  details?: Record<string, any>;
  traceId?: string;
}

// Generic Types for Services
export type ServiceResponse<T = any> = Promise<ApiResponse<T>>;
export type ServiceListResponse<T = any> = Promise<PaginatedResponse<T>>;

export interface CreateUserFormRequest {
  email: string;
  name?: string;
  pw1?: string;
  pw2?: string;
  /**
   * Legacy pre-arrears referral token (Base64), forwarded as-is.
   * Example: `YmFyY2xheXNfcHJlLWFycmVhcnM=`
   */
  qs?: string;
}

export interface CreateUserResponse extends LoginResponse {
  onboarding: {
    completed: boolean;
    currentStep: number;
  };
}

export interface SignInWithTokenRequest {
  token: string;
}

export interface SignInWithTokenResponse {
  access_token: string;
  refresh_token: string;
  user: User;
}

// Onboarding API Types
export interface OnboardingCustomerPayload {
  creationDate: string;
  lastUpdateDate: string;
  createdBy: number;
  lastUpdatedBy: number;
  id: number;
  address?: string;
  comments?: string;
  companyName?: string;
  customerId: number;
  freePostId: number;
  historyId: number;
  ieEncryptionString?: string;
  isEmailSent?: string;
  isPosted?: string;
  isTcAccepted?: string;
  notes?: string;
  referenceNumber?: string;
  shareNow?: string;
  submitDate?: string;

  // Field mappings from API specification
  heardAboutUs?: string;
  textField1?: string;
  textField2?: string;
  textField3?: string;
  textField4?: string;
  textField5?: string;
  field1Txtfld?: string;
  field2Txtfld?: string;
  field3TxtFld?: string;
  field4TxtFld?: CustomerConsent;
  field5Txtfld?: string;

  // Personal details
  customerTitle?: string;
  debtor1FirstName?: string;
  debtor1LastName?: string;
  debtor1DateOfBirthStr?: string;
  emailAddress?: string;
  mobileNumber?: string;
  buildingNumber?: string;
  postCode?: string;

  // Extended fields from API documentation
  buildingName?: string;
  streetName?: string;
  postTown?: string;
  landlineNumber?: string;
  customerConsent?: string;
  tncFlag?: string;
  tncDate?: string;
  jointAccount?: string;
  housingTenure?: string;
  dependentChildrenUnder16?: number;
  dependentChildrenAged16Plus?: number;
  haveChildrenUnder18?: string;
  adultsOver18IncludingMyself?: number;
  haveVehicle?: string;
  noOfVehiclesInHouseholds?: number;
  customerEmploymentStatus?: string;
  partnerEmploymentStatus?: string;
  debtor2Name?: string;
  country?: string;
  state?: string;
  userId?: number;
  lastLoginDate?: string;
  firstIeDate?: string;
  lastIeDate?: string;
  status?: string;
  submitDate2?: string;
  submittedBy?: string;
  submissionType?: string;
}

export interface OnboardingCustomerResponse {
  message: string;
  variant: "success" | "error" | "info";
  myData?: any;
}

export interface OnboardingUpdateRequest {
  customer: OnboardingCustomerPayload;
  metadata?: {
    source: "onboarding_flow";
    step: number;
    stepName: string;
    timestamp: string;
  };
}

export interface VulnerabilityCircumstancePayload {
  id?: number;
  customerId: number;
  field3TxtFld?: string;
  field4TxtFld?: string;
  field5Txtfld?: string;
  textColumn3?: string; // Speech, Language, or Communication Difficulties flag ('Y' or 'N')
}

export interface VulnerabilityCircumstanceResponse
  extends ApiResponse<
    VulnerabilityCircumstancePayload | VulnerabilityCircumstancePayload[] | null
  > {}

// Customer Field Stage Types
export interface CustomerFieldStageRecord {
  id?: number;
  customerId: number;
  fieldStage: string;
  userId?: number;
}

export interface CustomerFieldStageResponse
  extends ApiResponse<
    CustomerFieldStageRecord | CustomerFieldStageRecord[] | null
  > {}

// Budget/Financial API Types
// Energy Companies Types
export interface EnergyCompanyRecord {
  id?: number;
  companyName: string;
}

// Broadband Supplier Types
export interface BroadbandSupplierRecord {
  id?: number;
  supplierName: string;
}

// Landline Supplier Types
export interface LandlineSupplierRecord {
  id?: number;
  supplierName: string;
}

// Mobile Supplier Types
export interface MobileSupplierRecord {
  id?: number;
  supplierName: string;
}

// Water Company Types
export interface WaterCompanyRecord {
  id?: number;
  companyName: string;
  sharingMethod?: string;
  creationDate?: string;
  lastUpdateDate?: string;
  createdBy?: number;
  lastUpdatedBy?: number;
}

// Income Data Types
export interface IncomeDataRecord {
  creationDate: string;
  lastUpdateDate: string;
  createdBy: number;
  lastUpdatedBy: number;
  id: number;
  customerId: number;

  // Income fields
  basicIncome?: number;
  debtor2BasicIncome?: number;
  // Salary (legacy naming in API)
  otherSalaryWages1?: number;
  overtimeIncome?: number;
  debtor2OvertimeIncome?: number;
  investmentIncome?: number;
  debtor2InvestmentIncome?: number;
  dividends?: number;
  refundsAndReimbursements?: number;
  businessIncome?: number;
  pension?: number;
  childBenefit?: number;
  // Other income sources used in current schemas/mappings
  maintenanceOrChildSupport?: number;
  otherIncome1?: number;
  otherIncome2?: number;
  otherIncome3?: number;
  otherIncome4?: number;
  otherIncome5?: number;
  otherIncome6?: number;
  otherIncome7?: number;
  otherIncome8?: number;
  otherIncome9?: number;
  otherIncome1number?: number;

  // Benefits
  workingTaxCredit?: number;
  childTaxCredit?: number;
  empSupportAllowanceStatutory?: number;
  dlaPipAttendanceAllowance?: number;
  carerAllowance?: number;
  housingBenefitLocalAllowance?: number;
  councilTaxReduction?: number;
  otherIncludeUniversalCredit?: number;
  jobseekerAllowanceContribute?: number;
  incomeSupport?: number;

  // Pensions
  statePension?: number;
  privatePension?: number;
  pensionCredit?: number;
  pensionOthers?: number;

  // Assets
  totalValueProperties?: number;
  mortgageOutstanding?: number;
  securedLoanOutstanding?: number;
  valueOfVehicles?: number;
  assetSavings?: number;
  otherAssets?: number;

  // Other financial fields
  monthlySavingAmount?: number;
  monthlySavingsContribution?: number;
  monthlyTotalForCreditors?: number;

  // Savings and section-level toggles (Y/N flags stored as strings)
  // These mirror legacy IE Hub behaviour and drive yes/no UI toggles.
  isMonthlyContributionToSavings?: string;
  optOutSavingsContribution?: string;
  haveBenefits?: string;
  havePension?: string;
  haveProperties?: string;
  haveOtherIncome?: string;

  // Mode fields (frequency indicators)
  basicIncomeMode?: string;
  debtor2BasicIncomeMode?: string;
  bordersAndLodgerMode?: string;
  maintenanceOrChildSupportMode?: string;
  otherIncome1Mode?: string;
  nonDependantContributionsMode?: string;
  studentLoansAndGrantsMode?: string;
  jobseekerAllowanceContributeMode?: string;
  incomeSupportMode?: string;
  workingTaxCreditMode?: string;
  childTaxCreditMode?: string;
  carerAllowanceMode?: string;
  dalPipAttendanceAllowanceMode?: string;
  empSupportAllowanceStatutoryMode?: string;
  housingBenefitLocalAllowanceMode?: string;
  councilTaxReductionMode?: string;
  otherIncludeUniversalCreditMode?: string;
  otherBenefitsMode?: string;
  statePensionMode?: string;
  privatePensionMode?: string;
  pensionCreditMode?: string;
  pensionOthersMode?: string;
  childBenefitMode?: string;
  otherSalaryWages1Mode?: string;
}

export interface IncomeDataResponse extends ApiResponse<IncomeDataRecord[]> {}

export interface IncomeNoteData {
  lastUpdateDate: string;
  lastUpdatedBy: string;
  id: number;
  basicIncomeNotes: string;
  debtor2BasicIncomeNotes: string;
  bordersAndLodgerNotes: string;
  maintenanceOrChildSupportNotes: string;
  otherIncome1Notes: string;
  nonDependantContributionsNotes: string;
  studentLoansAndGrantsNotes: string;
  jobseekerAllowanceContributeNotes: string;
  incomeSupportNotes: string;
  workingTaxCreditNotes: string;
  childTaxCreditNotes: string;
  carerAllowanceNotes: string;
  dalPipAttendanceAllowanceNotes: string;
  empSupportAllowanceStatutoryNotes: string;
  housingBenefitLocalAllowanceNotes: string;
  councilTaxReductionNotes: string;
  otherIncludeUniversalCreditNotes: string;
  otherBenefitsNotes: string;
  statePensionNotes: string;
  privatePensionNotes: string;
  pensionCreditNotes: string;
  pensionOthersNotes: string;
  customerId: number;
  totalValuePropertiesNotes: string;
  mortgageOutstandingNotes: string;
  securedLoanOutstandingNotes: string;
  valueOfVehiclesNotes: string;
  assetSavingsNotes: string;
  otherAssetsNotes: string;
  childBenefitNotes: string;
  otherSalaryWages1Notes: string;
  monthlySavingAmountNotes: string;
}

export interface IncomeNoteResponse extends ApiResponse<IncomeNoteData[]> {}

export interface ExpenseDataRecord {
  lastUpdateDate: string;
  lastUpdatedBy: number;
  id: number;
  dcaId: number;
  groupId: number;
  beerAndAlcohol: number;
  childCare: number;
  cigarettesAndTobacco: number;
  clothing: number;
  councilTax: number;
  creditCardBills: number;
  electricityBill: number;
  emergencyFunds: number;
  entertainment: number;
  foodAndGroceries: number;
  friendsEvents: number;
  gasBill: number;
  gasoline: number;
  gifts: number;
  gymMembership: number;
  hairdressing: number;
  hobbiesLeisureAndSport: number;
  homeRepairs: number;
  homeSecurity: number;
  houseRent: number;
  householdInsurance: number;
  housekeeping: number;
  otherRentalAppliances: number;
  internetAccess: number;
  laundryBill: number;
  lifeInsurance: number;
  medicalHealthcare: number;
  mortgage: number;
  newspapersAndMagazines: number;
  otherExp1: number;
  otherExp2: number;
  otherExp3: number;
  otherExp4: number;
  otherExp5: number;
  otherExp6: number;
  otherExp7: number;
  otherExp8: number;
  otherExp9: number;
  otherExp1number: number;
  petCareAndFood: number;
  pocketMoneyAndSchoolTrips: number;
  propertyTaxes: number;
  publicTransport: number;
  savings: number;
  schoolCollUniversityFees: number;
  telephoneBill: number;
  travelAndVacation: number;
  tuitionFee: number;
  tvLicence: number;
  vehicleFuel: number;
  vehicleInsurance: number;
  vehicleMaintenance: number;
  vehicleTax: number;
  waterBill: number;
  asOnDate: string;
  customerNumber: string;
  secondMortgage: number;
  childMaintenance: number;
  courtOrders: number;
  mobile: number;
  health: number;
  houseMaintenance: number;
  insurance: number;
  planIfNoSettle: number;
  priorityExpenditure: string;
  otherSecuredLoans: number;
  buildingContentsInsurance: number;
  otherUtilities: number;
  hirePurchase: number;
  adultCareCosts: number;
  homePhone: number;
  otherPhone: number;
  otherPublicTransport: number;
  breakdownRecovery: number;
  parkingCharges: number;
  otherCarCosts: number;
  cleaningToileteries: number;
  housekeepingOthers: number;
  meals: number;
  lotteryAndPools: number;
  magistratesSheriffCourtFines: number;
  vetBillsAndPetInsurance: number;
  postage: number;
  holidays: number;
  otherEssentials1: number;
  otherEssentials2: number;
  otherEssentials3: number;
  nappiesAndBabyItems: number;
  groundRentAndServiceCharges: number;
  totalExpenseOnPriorityDebt: number;
  totalEssentialExpense: number;
  totalExpenseOnOthers: number;
  totalExpenseOnHousekeeping: number;
  totalExpenseOnTravel: number;
  totalExpenseOnPhone: number;
  totalOfTotalExpense: number;
  totalExpOnNonPriorityDebt: number;
  totalOfTotalIncome: number;
  postCode: string;
  adultCareCostsMode: string;
  beerAndAlcoholMode: string;
  breakdownRecoveryMode: string;
  buildingContentsInsuranceMode: string;
  childCareMode: string;
  childMaintenanceMode: string;
  cigarettesAndTobaccoMode: string;
  cleaningToileteriesMode: string;
  clothingMode: string;
  councilTaxMode: string;
  courtOrdersMode: string;
  electricityBillMode: string;
  gasBillMode: string;
  giftsMode: string;
  groundRentAndServiceChargesMode: string;
  hairdressingMode: string;
  healthMode: string;
  hirePurchaseMode: string;
  hobbiesLeisureAndSportMode: string;
  holidaysMode: string;
  homePhoneMode: string;
  homeRepairsMode: string;
  homeSecurityMode: string;
  householdInsuranceMode: string;
  housekeepingOthersMode: string;
  houseRentMode: string;
  insuranceMode: string;
  internetAccessMode: string;
  laundryBillMode: string;
  lifeInsuranceMode: string;
  lotteryAndPoolsMode: string;
  magistratesSheriffCourtFinesMode: string;
  mealsMode: string;
  mobileMode: string;
  mortgageMode: string;
  nappiesAndBabyItemsMode: string;
  newspapersAndMagazinesMode: string;
  otherCarCostsMode: string;
  otherEssentials1Mode: string;
  otherEssentials2Mode: string;
  otherEssentials3Mode: string;
  otherPhoneMode: string;
  otherPublicTransportMode: string;
  otherRentalAppliancesMode: string;
  otherSecuredLoansMode: string;
  otherUtilitiesMode: string;
  parkingChargesMode: string;
  petCareAndFoodMode: string;
  pocketMoneyAndSchoolTripsMode: string;
  postageMode: string;
  publicTransportMode: string;
  secondMortgageMode: string;
  tvLicenceMode: string;
  vehicleFuelMode: string;
  vehicleInsuranceMode: string;
  vehicleMaintenanceMode: string;
  vehicleTaxMode: string;
  vetBillsAndPetInsuranceMode: string;
  waterBillMode: string;
  foodAndGroceriesMode: string;
  expandAllDebts: string;
  expandAllExpenditure: string;
  expandAllHousekeeping: string;
  expandAllOtherExp: string;
  expandAllTravel: string;
  postcodeTrimmed: string;
  customerId: number;
  repaymentNonPriorityDebts: number;
  repaymentPriorityDebts: number;
  priorityDebts: number;
  nonPriorityDebts: number;
  debtAdminFee: number;
  debtAdminFeeMode: string;
  otherHomeCosts: number;
  otherExpUtilities: number;
  waterWaste: number;
  prescriptionAndMedicine: number;
  otherHealthCosts: number;
  schoolUniform: number;
  clubsAndSchoolTrips: number;
  otherSchoolCosts: number;
  pensionPayments: number;
  protectionInsurance: number;
  healthInsurance: number;
  otherPensionCosts: number;
  professionalCourses: number;
  unionFees: number;
  professionalFees: number;
  otherProfessionalCosts: number;
  otherEssentialCosts: number;
  otherCommunicationCosts: number;
  otherPersonalCosts: number;
  otherHomeCostsMode: string;
  otherExpUtilitiesMode: string;
  waterWasteMode: string;
  prescriptionAndMedicineMode: string;
  otherHealthCostsMode: string;
  schoolUniformMode: string;
  clubsAndSchoolTripsMode: string;
  otherSchoolCostsMode: string;
  pensionPaymentsMode: string;
  protectionInsuranceMode: string;
  healthInsuranceMode: string;
  otherPensionCostsMode: string;
  professionalCoursesMode: string;
  unionFeesMode: string;
  professionalFeesMode: string;
  otherProfessionalCostsMode: string;
  otherEssentialCostsMode: string;
  otherCommunicationCostsMode: string;
  otherPersonalCostsMode: string;
  totalOutgoingsFixed: number;
  totalOutgoingsFlexible: number;
  expandAllTotalOutgoingsFixed: string;
  expandAllTotalOutgoingsFlexible: string;
  housingFromDashboard: string;
  healthFromDashboard: string;
  travelFromDashboard: string;
  educationFromDashboard: string;
  miscFromDashboard: string;
  commsLeisureFromDashboard: string;
  foodHousekeepingFromDashboard: string;
  personalFromDashboard: string;
  rentFromDashboard: string;
  commAndLeisureFromSource: string;
  foodHousekeepingFromSource: string;
  personalCostsFromSource: string;
  mortgageOwing: number;
  hirePurchaseOwing: number;
  otherSecuredLoansOwing: number;
  haveHomeAndContents: string;
  haveUtilityCosts: string;
  haveCareAndHealthCosts: string;
  haveTransportAndTravelExpenses: string;
  haveSchoolExpenses: string;
  havePensionAndInsuranceExpenses: string;
  haveProfessionalExpenses: string;
  haveOtherExpenses: string;
  haveCommsAndLeisureExpenses: string;
  haveFoodAndHousekeepingExpenses: string;
  havePersonalCosts: string;
  havePriorityDebts: string;
  haveOtherPriorityDebts: string;
  haveNonPriorityDebts: string;
  haveOtherNonPriorityDebts: string;
  field4TxtFld: string;
  field5TxtFld: string;
  field4Num: number;
  field5Num: number;
  field4Dt: string;
  field5Dt: string;
  gasSupplier: string;
  electricitySupplier: string;
  awarePriorityServicess: string;
  awareCarbonMonoxidePoison: string;
  landlineSupplier: string;
  broadbandSupplier: string;
  mobileSupplier: string;
  gasSupplierOther: string;
  electricitySupplierOther: string;
  landlineSupplierOther: string;
  broadbandSupplierOther: string;
  mobileSupplierOther: string;
  hasDebtAdminFee: string;
  gamblingFromHobbiesLeisure: number;
  gamblingFromHobbiesLeisureMode: string;
}

export interface ExpenseDataResponse extends ApiResponse<ExpenseDataRecord[]> {}

export interface ExpensesNoteData {
  lastUpdateDate: string;
  lastUpdatedBy: string;
  id: number;
  adultCareCostsNotes: string;
  beerAndAlcoholNotes: string;
  breakdownRecoveryNotes: string;
  buildingContentsInsuranceNotes: string;
  childCareNotes: string;
  childMaintenanceNotes: string;
  cigarettesAndTobaccoNotes: string;
  cleaningToileteriesNotes: string;
  clothingNotes: string;
  councilTaxNotes: string;
  courtOrdersNotes: string;
  electricityBillNotes: string;
  gasBillNotes: string;
  giftsNotes: string;
  groundRentAndServiceChargesNotes: string;
  hairdressingNotes: string;
  healthNotes: string;
  hirePurchaseNotes: string;
  hobbiesLeisureAndSportNotes: string;
  holidaysNotes: string;
  homePhoneNotes: string;
  homeRepairsNotes: string;
  homeSecurityNotes: string;
  householdInsuranceNotes: string;
  housekeepingOthersNotes: string;
  houseRentNotes: string;
  insuranceNotes: string;
  internetAccessNotes: string;
  laundryBillNotes: string;
  lifeInsuranceNotes: string;
  lotteryAndPoolsNotes: string;
  magistratesSheriffCourtFinesNotes: string;
  mealsNotes: string;
  mobileNotes: string;
  mortgageNotes: string;
  nappiesAndBabyItemsNotes: string;
  newspapersAndMagazinesNotes: string;
  otherCarCostsNotes: string;
  otherEssentials1Notes: string;
  otherEssentials2Notes: string;
  otherEssentials3Notes: string;
  otherPhoneNotes: string;
  otherPublicTransportNotes: string;
  otherRentalAppliancesNotes: string;
  otherSecuredLoansNotes: string;
  otherUtilitiesNotes: string;
  parkingChargesNotes: string;
  petCareAndFoodNotes: string;
  pocketMoneyAndSchoolTripsNotes: string;
  postageNotes: string;
  publicTransportNotes: string;
  secondMortgageNotes: string;
  tvLicenceNotes: string;
  vehicleFuelNotes: string;
  vehicleInsuranceNotes: string;
  vehicleMaintenanceNotes: string;
  vehicleTaxNotes: string;
  vetBillsAndPetInsuranceNotes: string;
  waterBillNotes: string;
  foodAndGroceriesNotes: string;
  customerId: number;
  debtAdminFeeNotes: string;
  otherHomeCostsNotes: string;
  otherExpUtilitiesNotes: string;
  waterWasteNotes: string;
  prescriptionAndMedicineNotes: string;
  otherHealthCostsNotes: string;
  schoolUniformNotes: string;
  clubsAndSchoolTripsNotes: string;
  otherSchoolCostsNotes: string;
  pensionPaymentsNotes: string;
  protectionInsuranceNotes: string;
  healthInsuranceNotes: string;
  otherPensionCostsNotes: string;
  professionalCoursesNotes: string;
  unionFeesNotes: string;
  professionalFeesNotes: string;
  otherProfessionalCostsNotes: string;
  otherEssentialCostsNotes: string;
  otherCommunicationCostsNotes: string;
  otherPersonalCostsNotes: string;
}

export interface ExpensesNoteResponse extends ApiResponse<ExpensesNoteData[]> {}

// Guidance Notes Types
export interface GuidanceNoteRecord {
  fieldNameInAPI: string;
  fieldName: string;
  guidanceNotes: string;
}

export interface GuidanceNotesResponse
  extends ApiResponse<GuidanceNoteRecord[]> {}

// Income Triggers Types
export interface IncomeTriggerRecord {
  creationDate: string;
  lastUpdateDate: string;
  createdBy: number;
  lastUpdatedBy: number;
  id: number;
  incomeType: "Asset" | "Income" | "Expense";
  fieldName: string;
  columnName: string;
  upperLimit?: number;
  lowerLimit?: number;
  eldestChild?: number;
  additionalChild?: number;
}

export interface IncomeTriggersResponse
  extends ApiResponse<IncomeTriggerRecord[]> {}

// Action Types for UI Event Logging
export enum ActionType {
  BUTTON_CLICK = "BUTTON_CLICK",
  LINK_CLICK = "LINK_CLICK",
  TAB_CLICK = "TAB_CLICK",
  NAVIGATION = "NAVIGATION",
}

// Button Action Log Types
export interface ButtonActionLogRequest {
  actionType: ActionType;
  actionName: string;
  pageName:
    | "Income"
    | "Assets"
    | "Fixed expenses"
    | "Flexible expenses"
    | "Debts"
    | "Support"
    | "Dashboard"
    | string;
  subSection: string;
}

export interface ButtonActionLogRecord {
  creationDate: string;
  lastUpdateDate: string;
  createdBy: number;
  lastUpdatedBy: number;
  id: number;
  actionType: ActionType;
  actionName: string;
  pageName: string;
  subSection: string;
}

export interface ButtonActionLogResponse
  extends ApiResponse<ButtonActionLogRecord> {}

// Contact Us Types
export interface ContactUsRequest {
  contactUsInfo: string;
  message: string;
}

export interface ContactUsResponse {
  contactUsInfo: string;
  message: string;
}

// Income Update Types
export interface IncomeUpdateRequest extends Partial<IncomeDataRecord> {
  // Mandatory fields for updates
  id: number;
  customerId: number;
  lastUpdatedBy: number;
  lastUpdateDate: string;
}

export interface IncomeUpdateResponse extends ApiResponse<IncomeDataRecord> {}

type IncomeNoteFieldMap = Omit<
  IncomeNoteData,
  "id" | "customerId" | "lastUpdateDate" | "lastUpdatedBy"
>;

export interface IncomeNoteUpdateRequest extends Partial<IncomeNoteFieldMap> {
  // Mandatory fields for updates
  id: number;
  customerId: number;
  lastUpdatedBy: number;
  lastUpdateDate: string;
}

export interface ExpenseUpdateRequest extends Partial<ExpenseDataRecord> {
  // Mandatory fields for updates
  id: number;
  customerId: number;
  lastUpdatedBy: number;
  lastUpdateDate: string;
}

export interface ExpenseUpdateResponse extends ApiResponse<ExpenseDataRecord> {}

type ExpensesNoteFieldMap = Omit<
  ExpensesNoteData,
  "id" | "customerId" | "lastUpdateDate" | "lastUpdatedBy"
>;

export interface ExpensesNoteUpdateRequest
  extends Partial<ExpensesNoteFieldMap> {
  // Mandatory fields for updates
  id: number;
  customerId: number;
  lastUpdatedBy: number;
  lastUpdateDate: string;
}

export interface ExpensesNoteUpdateResponse
  extends ApiResponse<ExpensesNoteData> {}

// Dashboard API Types

// Dashboard Summary DTO - Transformed from Income data for UI display
export interface DashboardSummaryDto {
  balanceAmount: number;
  totalIncome: number;
  totalAssets: number;
  totalOutgoingsFixed: number;
  totalOutgoingsFlexible: number;
  totalOwingOnPriorityDebt: number;
  totalOwingOnNonPriorityDebt: number;
  totalOwingOnDebts: number;
  firstIeDate?: string;
  lastIeDate?: string;
  sectionsCompleted: number;
  totalFieldsToBeFilled: number;
  totalFieldsFilled: number;
  completionPercentage: number;
}

// Progress Section Definition
export interface ProgressSection {
  key: string;
  label: string;
  totalFields: number;
  completedFields: number;
  icon: string;
}

// Dashboard Button Action Log Types (extends existing ButtonActionLogRequest)
export interface DashboardButtonActionRequest extends ButtonActionLogRequest {
  pageName: "Dashboard";
  subSection:
    | "Welcome"
    | "BudgetSummary"
    | "Services"
    | "Share"
    | "OpenBanking"
    | "Benefits"
    | "WaterSupport"
    | "Marketing"
    | "Survey";
}

// Legacy Service Adapters (for optional integrations)
export interface CustomerProfileData {
  customerId: number;
  userId?: number;

  // Personal details
  title?: string;
  firstName?: string;
  surname?: string;
  debtor1FirstName?: string; // Legacy field name
  debtor1LastName?: string; // Legacy field name
  dateOfBirth?: string;
  debtor1DateOfBirthStr?: string; // Legacy field name

  // Contact details
  emailId?: string;
  emailAddress?: string; // Legacy field name
  phoneNumber?: string;
  mobileNumber?: string; // Legacy field name
  landlineNumber?: string; // Legacy field name

  // Address details
  addressLine1?: string;
  addressLine2?: string;
  buildingNumber?: string;
  buildingName?: string;
  streetName?: string;
  postTown?: string;
  city?: string;
  postcode?: string;
  postCode?: string; // Legacy field name
  state?: string;
  country?: string;

  // Accommodation
  accommodationType?: string;
  accommodationTypeCode?: string;
  housingTenure?: string; // Legacy field name

  // Employment
  employmentStatus?: string;
  employmentStatusCode?: string;
  customerEmploymentStatus?: string; // Legacy field name

  // Partner & Household
  isCompletingWithPartner?: boolean;
  jointAccount?: string; // Legacy field name
  partnerFirstName?: string;
  partnerSurname?: string;
  partnerDateOfBirth?: string;
  debtor2Name?: string; // Legacy field name
  debtor2DateOfBirthStr?: string; // Legacy field name
  debtor2DateOfBirth?: string; // Legacy field name
  partnerEmploymentStatus?: string;
  partnerEmploymentStatusCode?: string;

  // Household details
  dependantsUnder16?: number;
  dependantsUnder16Count?: number; // Legacy field name
  dependentChildrenUnder16?: number; // Legacy field name
  haveChildrenUnder18?: string; // Legacy field name
  adultsOver18IncludingMyself?: number; // Legacy field name
  vehicles?: number;
  vehiclesCount?: number; // Legacy field name
  haveVehicle?: string; // Legacy field name
  noOfVehiclesInHouseholds?: number; // Legacy field name

  // Circumstances
  circumstanceCategoryCodes?: string[];
  consent?: string;
  consentText?: string; // Legacy field name
  customerConsent?: string; // Legacy field name
  field3TxtFld?: string; // Vulnerability circumstances audience
  field4TxtFld?: CustomerConsent; // Health data sharing consent

  // Free-text fields used across legacy screens
  // - textField1: Customer additional notes (Budget page "Additional Notes")
  textField1?: string;

  // Metadata
  lastUpdateDate?: string;
  creationDate?: string;
  status?: string;
}

export interface ProfileLookupsResponse {
  accommodationTypes?: Array<{
    code: string;
    label: string;
    description?: string;
  }>;
  employmentStatuses?: Array<{
    code: string;
    label: string;
    description?: string;
  }>;
  titles?: Array<{
    code: string;
    label: string;
  }>;
  circumstances?: Array<{
    code: string;
    label: string;
    description?: string;
  }>;
}

export interface GuidanceNotesResponse {
  notes?: Array<{
    fieldName: string;
    guidance: string;
    helpText?: string;
  }>;
}

export interface ShareHistoryRecord {
  shareDate: string;
  shareType: string;
  target: string;
  status: string;
}

export interface MarketingServiceData {
  isOfferMoneyWellness?: boolean;
  marketingServiceUrl?: string;
  campaignData?: Record<string, any>;
}

export interface CustomerSupportData {
  field3TxtFld?: string;
  field4TxtFld?: CustomerConsent;
  waterCompany?: string;
  waterSupportEntitlement?: boolean;
  waterSureEntitlement?: boolean;
}

export interface SurveyData {
  surveyId?: string;
  surveyTitle?: string;
  surveyUrl?: string;
  isAvailable?: boolean;
}

// Dashboard Service Response Types
export interface DashboardDataResponse
  extends ApiResponse<{
    summary: DashboardSummaryDto;
    progressSections: ProgressSection[];
    budgetBreakdown: {
      kpiTiles: Record<
        string,
        {
          value: number;
          label: string;
          trend: "positive" | "negative" | "warning" | "neutral";
          format: "currency" | "percentage";
        }
      >;
      charts: {
        expenseBreakdown: any;
        cashFlowComparison: any;
      };
      insights: Array<{
        type: "info" | "warning" | "success";
        title: string;
        message: string;
        action: string;
        priority: "low" | "medium" | "high";
      }>;
      summary: {
        totalIncome: number;
        totalExpenses: number;
        totalAssets: number;
        totalDebts: number;
        monthlyBalance: number;
        savingsRate: number;
        debtToIncomeRatio: number;
        isPositiveBalance: boolean;
        hasEmergencyFund: boolean;
        needsBudgetReview: boolean;
      };
    };
    services: {
      benefits: { available: boolean; extraBenefitAmount?: number };
      waterSupport: {
        available: boolean;
        company?: string;
        entitlements?: string[];
      };
      openBanking: { available: boolean };
      marketing: { available: boolean; data?: MarketingServiceData };
    };
    share: {
      history?: ShareHistoryRecord[];
      canShare: boolean;
    };
    survey?: SurveyData;
  }> {}

// Budget Breakdown specific types
export interface BudgetBreakdownKPI {
  value: number;
  label: string;
  trend: "positive" | "negative" | "warning" | "neutral";
  format: "currency" | "percentage";
}

export interface BudgetInsight {
  type: "info" | "warning" | "success";
  title: string;
  message: string;
  action: string;
  priority: "low" | "medium" | "high";
}

export interface ChartData {
  labels: string[];
  datasets: Array<{
    label?: string;
    data: number[];
    backgroundColor?: string[];
    borderWidth?: number;
  }>;
}

export interface UploadDocumentResponse {
  creationDate: string;
  lastUpdateDate: string;
  createdBy: number;
  lastUpdatedBy: number;
  id: number;
  customerId: number;
  documentId: number;
  documentName: string;
  documentSize: string;
  fileName: string;
  shareId: string;
  userId: number;
  uploadDate: string;
  isDocUploadedFromDecl: string;
}

export interface UcEvidencePeriod {
  id: number;
  customerId: number;
  day?: string;
  month?: string;
  year?: string;
  userId: number;
  entrySource: string;
  entrySourceLabel?: string | null;
  entrySourceDescription?: string | null;
  creationDate?: string;
  lastUpdateDate?: string;
  createdBy?: number;
  lastUpdatedBy?: number;
}

export interface UcEvidenceUpsertRequest {
  id?: number;
  customerId: number;
  day: string;
  month: string;
  year: string;
  userId: number;
  entrySource: string;
  entrySourceLabel?: string | null;
  entrySourceDescription?: string | null;
}

export interface FreePostDataResponse {
  lastUpdatedBy: number;
  address: string;
  notes: string;
  createdBy: number;
  referenceNumber: string;
  lastUpdateDate: string;
  companyName: string;
  customerId: number;
  id: number;
  creationDate: string;
  shareNow: string;
  // Optional linkage back to debts and referral flows
  debtId?: number;
  source?: string | null;
}

export interface CustomerSharingResponse {
  companyName?: string;
  shareAgain: boolean;
  lastUpdatedBy: number;
  lastUpdateDate: string;
  subDomain: string;
  isDelete: boolean;
  hasCreditorPortal: string;
  accountNumber: string;
  creationDate: string;
  schemaName: string;
  styleClass: string;
  isSharing: string;
  isEditable: boolean;
  createdBy: number;
  lenderName: string;
  customerId: number;
  revokeBtn: boolean;
  creditor: string;
  id: number;
  shareNow: string;
  lenderOrgId: number;
  submitDate?: string;
  lastSharedDate?: string;
  isDeleted?: string;
  // Optional linkage back to debts and referral flows
  debtId?: number;
  source?: string | null;
  submissionType?: string;
  field5TxtFld?: string;
}

export type DebtFrequencyApi =
  | "W"
  | "2W"
  | "4W"
  | "M"
  | "Q"
  | "SA"
  | "Y"
  | string;

export type DebtSource =
  | "customer_debt"
  | "customer_expenses"
  | "arrears"
  | string;

export interface CustomerDebtRecord {
  id: number;
  customerId: number;
  creditor: string;
  accTypeCode?: string | null;
  accountTypeName?: string | null;
  accountNumber?: string | null;
  amountOwed?: number | null;
  repayment?: number | null;
  frequency?: DebtFrequencyApi | null;
  repaymentMonthly?: number | null;
  debtType?: string | null;
  shareBudget?: "Y" | "N" | null;
  source?: DebtSource;
  notes?: string | null;
  isDigital?: "Y" | "N" | null;
  parentCompanyName?: string | null;
  lenderName?: string | null;
  schemaName?: string | null;
  shareNow?: "Y" | "N" | null;
  paymentOfferAmount?: number | null;
  paymentOfferMode?: DebtFrequencyApi | null;
  createdBy?: number | null;
  creationDate?: string | null;
  lastUpdatedBy?: number | null;
  lastUpdateDate?: string | null;
  [key: string]: unknown;
}

export interface DebtUpsertRequest {
  id?: number;
  customerId: number;
  creditor: string;
  accTypeCode?: string | null;
  accountNumber?: string | null;
  amountOwed?: number | null;
  repayment?: number | null;
  frequency?: DebtFrequencyApi | null;
  notes?: string | null;
  shareBudget?: "Y" | "N" | null;
  source?: DebtSource;
  debtType?: string | null;
  repaymentMonthly?: number | null;
}

export interface DebtListResponse extends ApiResponse<CustomerDebtRecord[]> {}

export interface DebtResponse extends ApiResponse<CustomerDebtRecord> {}

export interface PaymentOfferRecord {
  id?: number;
  customerId: number;
  debtId: number;
  lenderName: string;
  schemaName?: string | null;
  subDomain?: string | null;
  accountNumber?: string | null;
  paymentOfferAmount?: number | null;
  paymentOfferMode?: DebtFrequencyApi | null;
  paymentPreferredFrequency?: DebtFrequencyApi | null;
  shareNow?: "Y" | "N" | null;
  createdBy?: number | null;
  creationDate?: string | null;
  lastUpdatedBy?: number | null;
  lastUpdateDate?: string | null;
}

export interface PaymentOfferUpsertRequest {
  customerId: number;
  debtId?: number;
  lenderName: string;
  accountNumber?: string | null;
  schemaName?: string | null;
  paymentOfferAmount?: number | null;
  paymentOfferMode?: DebtFrequencyApi | null;
  paymentPreferredFrequency?: DebtFrequencyApi | null;
  shareNow?: "Y" | "N" | null;
  /**
   * Optional audit/user fields. Some backend environments may ignore these, but
   * when supported they help attribute updates.
   */
  userId?: number | null;
  createdBy?: number | null;
  lastUpdatedBy?: number | null;
  id?: number;
}

export interface PaymentOfferResponse
  extends ApiResponse<PaymentOfferRecord | PaymentOfferRecord[]> {}

export interface FreePostSharingPayload {
  companyName: string;
  referenceNumber: string;
  customerId: number;
  debtId?: number;
  accountNumber?: string | null;
  address?: string | null;
  notes?: string | null;
  shareNow?: "Y" | "N";
  isTcAccepted?: "Y" | "N";
  lenderName?: string | null;
  schemaName?: string | null;
  subDomain?: string | null;
  /**
   * Optional source marker for Freepost sharing entries.
   * Examples: "additional-support", "referred".
   */
  source?: string | null;
  id?: number;
  lastUpdateDate?: string;
}

export interface CustomerSharingUpsertRequest {
  customerId: number;
  debtId?: number;
  lenderName: string;
  /** Same as orgId; returned on sharing GET and expected on some PATCH/POST bodies. */
  lenderOrgId?: number | string | null;
  accountNumber?: string | null;
  schemaName?: string | null;
  subDomain?: string | null;
  /**
   * Optional source marker for digital sharing entries.
   * - "referred"           → sharing initiated via referral/profile flows
   * - "additional-support" → sharing initiated via Additional Support flows
   * - other string values  → reserved for future categorisation
   */
  source?: string | null;
  shareNow?: "Y" | "N";
  isSharing?: "Y" | "N";
  id?: number;
}

export type DebtCategoryKey =
  | "creditCards"
  | "loans"
  | "overdrafts"
  | "otherDebts"
  | "arrears";

export type DebtRepaymentFrequencyUi =
  | "weekly"
  | "2weekly"
  | "4weekly"
  | "monthly"
  | "quarterly"
  | "semiAnnual"
  | "yearly"
  | string;

export interface DebtFormEntry {
  id?: number | string;
  tempId?: string;
  category: DebtCategoryKey;
  creditorName: string;
  parentCompanyName?: string | null;
  lenderName?: string | null;
  accTypeCode?: string | null;
  accTypeId?: number | null;
  accountTypeName?: string | null;
  accountNumber?: string | null;
  amountOwed?: number | null;
  repaymentAmount?: number | null;
  repaymentFrequency?: DebtRepaymentFrequencyUi | null;
  paymentOfferAmount?: number | null;
  paymentOfferFrequency?: DebtRepaymentFrequencyUi | null;
  paymentOfferId?: number | null;
  paymentOfferMode?: DebtFrequencyApi | null;
  shareBudget?: "Y" | "N" | null;
  shareNow?: "Y" | "N" | null;
  isDigital?: boolean;
  schemaName?: string | null;
  subDomain?: string | null;
  isPriority?: boolean;
  source?: DebtSource;
  debtType?: string | null;
  notes?: string | null;
  arrearsCode?: string | null;
  raw?: CustomerDebtRecord;
}

export interface DebtsSchemaState {
  haveDebts: boolean;
  creditCards: DebtFormEntry[];
  loans: DebtFormEntry[];
  overdrafts: DebtFormEntry[];
  otherDebts: DebtFormEntry[];
  arrears: DebtFormEntry[];
  allDebtsAdded?: boolean;
}

export interface UpdateDocumentRequest {
  creationDate?: string;
  lastUpdateDate?: string;
  createdBy?: number;
  lastUpdatedBy?: number;
  id: number | string;
  customerId: number;
  documentId: number;
  documentName: string;
  documentSize: string;
  fileName: string;
  shareId?: string;
  userId?: number;
  uploadDate?: string;
  isDocUploadedFromDecl?: string;
}

export interface DigitalLender {
  orgId: number | string | null;
  schemaName: string;
  creditor: string;
  subDomain: string;
  lender: string;
  referenceNumberFormat: string;
  customerPaymentOffer: string;
  showCompanyList: string;
  addToDebtList: string;
  parentCompany: string;
  creditorAddress: string;
  isCreditorExistsWithIeHub: string;
}

export interface Debt {
  creationDate: string;
  lastUpdateDate: string;
  createdBy: number;
  lastUpdatedBy: number;
  id: number;
  sessionId: string;
  customerId: number;
  userId: number;
  accSearchId: number;
  searchId: string;
  accNo: string;
  month: string;
  balance: number;
  accLimit: number;
  accStatusCode: string;
  paymentStatusCode: string;
  stmtBalance: number;
  paymentAmount: number;
  cashAdvCount: number;
  cashAdvTotal: number;
  amountOwed: number;
  repayment: number;
  creditor: string;
  frequency: string;
  accTypeCode: string;
  verified: string;
  notes: string;
  accStatus: string;
  accountNumber: string;
  shareBudget: string;
  source: string;
  mortgageOwingAmt: number;
  hirePurchaseAmt: number;
  utilityOwingAmt: number;
  securedDebtsAmt: number;
  repaymentMonthly: number;
  debtType: string;
  acctypecodecc: string;
  creditorcc: string;
  amountOwedcc: number;
  repaymentcc: number;
  frequencycc: string;
  notescc: string;
  doNotUpdateCustomer: string;
}

export interface TotalEnergyBills {
  totalDebtRepayEnergyBills: number;
  totalExpEnergyBills: number;
}

export interface CalculatedTotals {
  customerId: number;
  totalIncome: number;
  totalAssets: number;
  totalOutgoingsFixed: number;
  totalOutgoingsFlexible: number;
  totalOwingOnPriorityDebt: number;
  totalRepayOnPriorityDebt: number;
  totalOwingOnNonPriorityDebt: number;
  totalRepayOnNonPriorityDebt: number;
  pendingPaymentOffer: number;
  balanceAmount: number;
  totalRepaymentDebts: number;
  totalOwingOnDebts: number;
}

export interface PostcodeEntry {
  id: number;
  postcode: string;
  inUse: string;
  district: string;
  districtCode: string;
  country: string;
  postcodeArea: string;
  postcodeDistrict: string;
  postcodeTrimmed: string;
}

export interface AdditionalSupportEntry {
  companyName: string;
  customerId: number;
}

export interface SharedDocResponse {
  creationDate: string;
  lastUpdateDate: string;
  createdBy: number;
  lastUpdatedBy: number;
  id: number;
  customerId: number;
  documentId: number;
  companyName: string;
  referenceNumber: string;
  fileName: string;
  documentSize: string;
  shareId: string;
  isShared: string;
  isSubmitted: string;
  schemaName: string;
  documentName: string;
  subDomain: string;
  userId: number;
  uploadedBy: string;
  uploadedRefNumber: string;
  uploadDate?: string;
}

export interface LenderNotShared {
  id: number;
  description: string;
  objectId: number;
  orgId: number;
  ieEncryptionString: string;
  schemaName: string;
  textField1: string;
  textField2: string;
  textField3: string;
  subDomain: string;
  hasCreditorPortal: string;
  infoText: string;
  lender: string;
  reportType: string;
  sftpLocation: string;
  reportFormat: string;
  isDmc: string;
  referenceNumberFormat: string;
  customerPaymentOffer: string;
  isIehubSupporter: string;
  defaultDebtTypeCode: string;
  showCompanyList: string;
  addToDebtList: string;
  parentCompany: string;
  sftpLocation1: string;
  sftpLocation2: string;
  companyId: number;
  orderKey: number;
  isOriginator: string;
  originatorType: string;
  countryId: number;
  lenderEmailId: string;
}

export interface CustomerDeclarationEntry {
  creationDate: string;
  lastUpdateDate: string;
  createdBy: number;
  lastUpdatedBy: number;
  id: number;
  customerId: number;
  sharingId: number;
  declarationStatus: string;
  isPrimaryResidence: string;
  hasNegativeCircumstances: string;
  negativeCircumstancesDescription: string;
  hasPensionCreditInHousehold: string;
  allAdultsReceivingPension: string;
  hasMedicalConditionAffectingWaterUsage: string;
  accountNumber: string;
  customerAgreedDate: number;
  potentialChangeInCircumstances: string;
  potentialCircumstancesOther: string;
}

export interface CustomerLatestSnapshot {
  creationDate: string;
  lastUpdateDate: string;
  createdBy: number;
  lastUpdatedBy: number;
  id: number;
  customerId: number;
  balanceAmount: number;
}

export interface NotifyCustomerDebtAdvice {
  companyName: string;
  userId: string;
  debtAdviceContactPreNdl?: string;
  isUnitedUtilitiesCompany?: string;
  mobileNumber: string;
  notificationSource: string;
  notifyInformation?: string;
  payPlanChannel?: "EMAIL" | "PHONE" | "WHATSAPP";
  payPlanCallTime?: "Anytime" | "Morning" | "Afternoon" | "Evening";
}

export interface NotifyCustomerDebtAdviceResponse {
  userId: string;
  isUnitedUtilitiesCompany: string;
  notificationSource: string;
  notifyInformation: string;
}

export interface DeclarationAllEntry {
  id: number;
  creationDate: string;
  lastUpdateDate: string;
  createdBy: number;
  lastUpdatedBy: number;
  customerId: number;
  sharingId: number;
  declarationStatus: string;
  confirmDetails: string;
  processStoreDetails: string;
  neededPersonalDetails: string;
  documentConfirmatinYwct: string;
  awardNotRcvdConfirmationYwct: string;
  documentConfirmatinCder?: string;
  companyName: string;
  accountNumber: string;
}

export interface CustomerPreferencesEntry {
  creationDate: string;
  lastUpdateDate: string;
  createdBy: number;
  lastUpdatedBy: number;
  id: number;
  customerId: number;
  sharingId: number;
  preferredTimeForNdl: string;
  debtAdviceContactPreferenceNdl: string;
  voicemailConsentNdl: string;
  emailMeNdl: string;
  textMeNdl: string;
  whatsAppConsent?: string;
  companyName: string;
  accountNumber: string;
}

export interface ApplianceData {
  creationDate: string;
  lastUpdateDate: string;
  createdBy: number;
  lastUpdatedBy: number;
  id: number;
  areAllGasAppliancesWorking: string;
  locationGasCooker: string;
  makeOrModelGasCooker: string;
  dateOfServiceGasCooker: string;
  locationGasHob: string;
  makeOrModelGasHob: string;
  dateOfServiceGasHob: string;
  locationGasFire: string;
  makeOrModelGasFire: string;
  dateOfServiceGasFire: string;
  locationBoller: string;
  makeOrModelBoller: string;
  dateOfServiceBoller: string;
  companyName: string;
  accountNumber: string;
  customerId: number;
  sharingId: number;
  isResponsibleForMaintenance: string;
  gasCookerOrderKey: string;
  gasHobOrderKey: string;
  gasFireOrderKey: string;
  bollerOrderKey: string;
}

export interface DeclarationInfoData {
  creationDate: string;
  lastUpdateDate: string;
  createdBy: number;
  lastUpdatedBy: number;
  id: number;
  lender: string;
  infoText: string;
  declarationInformation: string;
}

export interface SmartReviewEntry {
  id?: number;
  customerId?: number;
  userId?: number;
  valProfileAgeEmploymentMismatch: string;
  valProfileBenefitAgeMismatch: string;
  valDependantsBenefitMismatch: string;
  valIncomeUnemployedSalary: string;
  valAccommodationParentCosts: string;
  valVehicleCostsNoVehicle: string;
  valVehicleNoCosts: string;
  valSavingsTooHigh: string;
  valDebtAmountsMissing: string;
  valIncomeSmartReviewDate: string;
}

export interface SendEmailOnShareEntry {
  companyName: string;
  customerId?: number;
}
